import { z } from 'zod';

import { saleOriginSchema } from '../subscription.schema';

// =============================================================================
//  CRM Deal schemas — Story 11.1 (fundação) + Story 11.4 (CRUD + lifecycle)
//  Fonte única back+front (arquitetura 08-fase3-crm-architecture.md § III.3, FR-51).
//  Funil FIXO (DR-039): CrmDealStage é enum, não FK.
//  CRUD + lifecycle open/won/lost + PUT /stage (optimistic lock) — Story 11.4.
// =============================================================================

export const crmDealStageSchema = z.enum([
  'novo_lead',
  'qualificacao',
  'diagnostico',
  'proposta_enviada',
  'negociacao',
  'fechamento_ganho',
  'fechamento_perdido',
]);
export type CrmDealStageSchema = z.infer<typeof crmDealStageSchema>;

export const crmDealStatusSchema = z.enum(['open', 'won', 'lost']);
export type CrmDealStatusSchema = z.infer<typeof crmDealStatusSchema>;

// Valor monetário com até 2 casas, positivo se preenchido (Decimal(10,2) — AC9).
// Deal sem valor é válido (null/undefined); se informado, deve ser > 0.
const moneyField = z
  .number()
  .positive('Valor deve ser maior que zero.')
  .max(99_999_999.99, 'Valor excede o limite.')
  .multipleOf(0.01, 'Valor deve ter no máximo 2 casas decimais.')
  .optional()
  .nullable();

// -----------------------------------------------------------------------------
// CREATE
// -----------------------------------------------------------------------------

export const createCrmDealSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, 'Título obrigatório.')
    .max(200, 'Título deve ter até 200 caracteres.'),
  lead_id: z.string().uuid('lead_id inválido.').optional().nullable(),
  client_id: z.string().uuid('client_id inválido.').optional().nullable(),
  saas_product_id: z.string().uuid('saas_product_id inválido.').optional().nullable(),
  plan_id: z.string().uuid('plan_id inválido.').optional().nullable(),
  value: moneyField,
  stage: crmDealStageSchema.default('novo_lead'),
  expected_close_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve estar no formato YYYY-MM-DD')
    .optional()
    .nullable(),
  responsible_user_id: z.string().uuid('responsible_user_id inválido.').optional().nullable(),
  origin: saleOriginSchema.optional().nullable(),
});
export type CreateCrmDealDto = z.output<typeof createCrmDealSchema>;
export type CreateCrmDealInput = z.input<typeof createCrmDealSchema>;

// Motivo de perda — obrigatório ao fechar perdido (AC6/AC9), min 5 chars.
const lostReasonField = z
  .string()
  .trim()
  .min(5, 'Motivo da perda deve ter ao menos 5 caracteres.')
  .max(500, 'Motivo da perda deve ter até 500 caracteres.');

// -----------------------------------------------------------------------------
// UPDATE — campos editáveis (NÃO inclui `stage`: stage só via PUT /stage, 11.5).
// `version` obrigatório (optimistic lock — DR-028, AC4).
// `status` aceito como atalho de fechamento ganho/perdido (AC5/AC6):
//   - status='won'  → fecha ganho
//   - status='lost' → fecha perdido (exige lost_reason, refinado abaixo)
// -----------------------------------------------------------------------------

const updateCrmDealBaseSchema = createCrmDealSchema
  .omit({ stage: true })
  .partial()
  .extend({
    status: crmDealStatusSchema.optional(),
    lost_reason: lostReasonField.optional().nullable(),
    version: z.coerce.number().int().positive('version é obrigatório (optimistic lock).'),
  })
  .strict();

export const updateCrmDealSchema = updateCrmDealBaseSchema.superRefine((data, ctx) => {
  // AC6/AC9: ao marcar lost via PUT, lost_reason é obrigatório (min 5 chars).
  if (data.status === 'lost' && (data.lost_reason === undefined || data.lost_reason === null)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['lost_reason'],
      message: 'Motivo da perda é obrigatório ao marcar como perdido.',
    });
  }
});
export type UpdateCrmDealDto = z.output<typeof updateCrmDealSchema>;
export type UpdateCrmDealInput = z.input<typeof updateCrmDealSchema>;

// -----------------------------------------------------------------------------
// STAGE — PUT /deals/:id/stage (11.5). Body `{ stage, version, reason? }`.
// Ao mover pra `fechamento_perdido`, `reason` vira `lost_reason` obrigatório.
// `version` obrigatório (optimistic lock — DR-028).
// -----------------------------------------------------------------------------

const changeCrmDealStageBaseSchema = z
  .object({
    stage: crmDealStageSchema,
    reason: lostReasonField.optional().nullable(),
    version: z.coerce.number().int().positive('version é obrigatório (optimistic lock).'),
  })
  .strict();

export const changeCrmDealStageSchema = changeCrmDealStageBaseSchema.superRefine((data, ctx) => {
  // AC6: mover pra fechamento_perdido exige reason (vira lost_reason).
  if (data.stage === 'fechamento_perdido' && (data.reason === undefined || data.reason === null)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['reason'],
      message: 'Motivo da perda é obrigatório ao mover para fechamento perdido.',
    });
  }
});
export type ChangeCrmDealStageDto = z.output<typeof changeCrmDealStageSchema>;
export type ChangeCrmDealStageInput = z.input<typeof changeCrmDealStageSchema>;

// -----------------------------------------------------------------------------
// QUERY — cursor pagination (DR-032); ?view=kanban tratado no controller.
// Filtros (AC2): status, stage, responsável, saas, origem, faixa de valor,
// "parado há X dias" (stalled_days).
// -----------------------------------------------------------------------------

export const queryCrmDealsSchema = z
  .object({
    stage: crmDealStageSchema.optional(),
    status: crmDealStatusSchema.optional(),
    responsible_user_id: z.string().uuid('responsible_user_id inválido.').optional(),
    saas_product_id: z.string().uuid('saas_product_id inválido.').optional(),
    origin: saleOriginSchema.optional(),
    lead_id: z.string().uuid('lead_id inválido.').optional(),
    client_id: z.string().uuid('client_id inválido.').optional(),
    /** Filtro por faixa de valor (Decimal). min/max não-negativos. */
    value_min: z.coerce.number().nonnegative('value_min não pode ser negativo.').optional(),
    value_max: z.coerce.number().nonnegative('value_max não pode ser negativo.').optional(),
    /** "Parado há X dias" — deals sem mudança de estágio há N dias (FR-51.6). */
    stalled_days: z.coerce.number().int().positive('stalled_days deve ser positivo.').optional(),
    view: z.enum(['kanban', 'list']).optional(),
    cursor: z.string().uuid('cursor inválido.').optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict()
  .superRefine((data, ctx) => {
    if (
      data.value_min !== undefined &&
      data.value_max !== undefined &&
      data.value_min > data.value_max
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['value_min'],
        message: 'value_min não pode ser maior que value_max.',
      });
    }
  });
export type QueryCrmDealsDto = z.output<typeof queryCrmDealsSchema>;
export type QueryCrmDealsInput = z.input<typeof queryCrmDealsSchema>;
